import React, {useContext} from 'react';
import {Link} from 'react-router-dom';
import {Context} from '../context';
import Button from './UI/Button';
import {getStationNameFromId, dollarAmount} from '../helperFunctions';

function cartPassesItemQuantity(cartPasses) {
  return cartPasses
    .map(item => item.quantity)
    .reduce((partialSum, a) => partialSum + a, 0);
}

const getCartOrPurchaseCartTotal = cartOrPurchaseCart => {
  const {passes, storedValue} = cartOrPurchaseCart;
  const passesTotal = passes
    .map(item => (item.price || 0) * (item.quantity || 0))
    .reduce((a, b) => a + b, 0);
  const storeValueTotal = storedValue !== 0 && storedValue;

  return dollarAmount(passesTotal + storeValueTotal);
};

export default function Cart() {
  const {
    cart,
    cartPassesSetQuantity,
    clearCartPasses,
    account,
    locale,
    localeText,
    checkout,
    setCheckout,
    purchase,
    paymentProcessing,
    getTicketName,
    content,
    showCartIsEmpty
  } = useContext(Context);

  const cartTitle = () => {
    const firstName = account?.profile?.firstName;

    if (firstName && locale === 'en') {
      return `${firstName}'s Cart`;
    }
    if (firstName && locale === 'es') {
      return `Carro de ${firstName}`;
    }
    return localeText('rw_cart_heading_your_cart', 'Your Cart');
  };

  const cartOrPurchaseCart = purchase.purchaseConfirmationPage
    ? purchase.cart
    : cart;

  return (
    <div className='border border-black rounded-md'>
      <div className='bg-wwPrimary text-white p-1 flex justify-center gap-4 items-center'>
        {!purchase.purchaseConfirmationPage ? (
          <React.Fragment>
            <img
              className='h-9'
              src='/images/shopping_cart.png'
              alt={localeText(
                'rw_cart_alt_shopping_cart_icon',
                'Shopping cart icon'
              )}
            />
            <h2 className=''>{cartTitle()}</h2>
            <div className='text-black bg-white px-2 rounded-md'>
              {cartPassesItemQuantity(cart?.passes || [])}
            </div>
          </React.Fragment>
        ) : (
          <h2>Your Purchase</h2>
        )}
      </div>

      <div className='p-4'>
        {showCartIsEmpty ? (
          <p className='text-center'>
            {localeText(
              'rw_cart_text_empty_cart',
              'You have no items in your cart.'
            )}
          </p>
        ) : (
          <div>
            {cartOrPurchaseCart?.storedValue !== 0 && (
              <div className='flex flex-row justify-between text-2xl pb-5 text-red'>
                <div className=''>Negative Account Balance</div>
                <div className=' font-bold'>
                  ${(cartOrPurchaseCart.storedValue / 100).toFixed(2)}
                </div>
              </div>
            )}
            {cartOrPurchaseCart?.passes.map((item, index) => (
              <div className='pb-4 mt-4 first:mt-0' key={index}>
                <div className='flex justify-between gap-2'>
                  <div>
                    {item.id && (
                      <div>
                        <div className='font-semibold'>
                          {
                            account?.transitAccounts?.[0]?.transitCards?.find(
                              transitCard => transitCard.uuid === item.uuid
                            ).nickname
                          }
                        </div>
                        <div>{getTicketName(item.id)}</div>
                      </div>
                    )}
                  </div>
                  <div className='flex flex-col justify-between items-end'>
                    <div className='flex'>
                      {purchase.purchaseConfirmationPage ? (
                        item.quantity
                      ) : (
                        <React.Fragment>
                          <button
                            onClick={() => {
                              cartPassesSetQuantity(item, item.quantity - 1);
                            }}
                            type='button'
                            className='border border-bg-wwPrimary px-2 rounded-md'
                          >
                            -
                          </button>
                          <span className='px-4'>{item.quantity}</span>
                        </React.Fragment>
                      )}
                    </div>
                  </div>
                </div>
                {(item.origin_id || item.destination_id) && (
                  <div>
                    {getStationNameFromId(localeText, content, item.origin_id)}{' '}
                    -{' '}
                    {getStationNameFromId(
                      localeText,
                      content,
                      item.destination_id
                    )}
                  </div>
                )}
                <div className='w-full flex justify-between mt-2'>
                  <h4 className='m-0'>
                    {dollarAmount(item.price)} /{' '}
                    {localeText('rw_title_each', 'ea')}
                  </h4>
                  <h3 className='m-0'>
                    {dollarAmount((item.price || 0) * item.quantity)}
                  </h3>
                </div>
              </div>
            ))}

            {!purchase.purchaseConfirmationPage && (
              <Link
                to='/buy'
                onClick={clearCartPasses}
                className='flex items-center gap-2 py-2'
                id='clearCart'
              >
                <img
                  className='h-4'
                  src='/images/clearall.png'
                  alt={localeText(
                    'rw_cart_alt_clear_cart_icon',
                    'Clear cart icon'
                  )}
                />
                <h4 className='my-0'>
                  {localeText('rw_button_clear_cart', 'Clear Cart')}
                </h4>
              </Link>
            )}
            <hr />
            <div className='flex items-baseline justify-between'>
              <h3 className=''>
                {localeText('rw_cart_title_total', 'Total')}:
              </h3>
              <h2 className='font-bold'>
                {getCartOrPurchaseCartTotal(cartOrPurchaseCart)}
              </h2>
            </div>
            {!purchase.purchaseConfirmationPage &&
              (!checkout ? (
                <Link className='w-full flex' to='/checkout' id='checkout'>
                  <Button
                    buttonId='checkout'
                    className='bg-wwLight w-full'
                    onClick={() => setCheckout(true)}
                  >
                    {localeText('rw_cart_button_checkout', 'Checkout')}
                  </Button>
                </Link>
              ) : (
                <Link className='w-full flex' to='/buy' id='buy'>
                  <Button
                    loading={paymentProcessing}
                    className='bg-wwLight w-full'
                    onClick={() => setCheckout(false)}
                  >
                    {localeText('rw_cart_button_edit', 'Edit')}
                  </Button>
                </Link>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
